import { EscalationStatus, UserInterface } from "@swivl-mono/types";
import { SessionMessageData } from "apps/studio/src/Hooks/useSessionMessageData";
import { AnimatePresence, motion } from "motion/react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Member } from "../../../../Models/Member/Member.Current";
import { UserConversationsInterface, UsersModel } from "../../../../Models/Users/Users.Model";
import { OpenConversationCard } from "../Components/Conversations.OpenConversationCard";

interface ConversationsOpenUserListProps {
  conversations?: { [userId: number]: UserConversationsInterface };
  sessionMessageData?: { [userId: number]: { [sessionId: number]: SessionMessageData } };
  searchTerm?: string;
  selectedUserId?: number;
  allowedLocationsMap?: { [locationId: string]: boolean }; // Map of location IDs to enabled status
}

const matchesSearchTerm = (user?: UserInterface, searchTerm?: string) => {
  if (!searchTerm || !searchTerm.length) { return true; }
  if (!user || !user.name) return false;
  return user.name.toLowerCase().includes(searchTerm.toLowerCase());
}

const sortConversationsByMostRecentMessage = (conversations: UserConversationsInterface[]) => {
  return conversations.sort((a, b) => {
    const aDate = a.lastUserMessageAt
    const bDate = b.lastUserMessageAt
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return bDate.getTime() - aDate.getTime();
  });
}

export function ConversationsOpenUserList(props: ConversationsOpenUserListProps) {
  const { usersOnline } = UsersModel.useState((s) => s);
  const { conversations, searchTerm, selectedUserId, sessionMessageData, allowedLocationsMap } = props;

  const { escalatedConversations, otherConversations } = useMemo(() => {
    if (!conversations) return { escalations: [], otherConversations: [] };

    let escalatedConversations: UserConversationsInterface[] = [];
    let otherConversations: UserConversationsInterface[] = [];

    for (const [_, conversation] of Object.entries(conversations)) {
      if (!allowedLocationsMap) {
        return { escalatedConversations, otherConversations };
      }
      if ((conversation?.user as any)?.deletedDate) {
        console.log('skipping deleted user conversation :', conversation?.user?.id);
        continue; // Skip conversations from locations that are not allowed
      }
      if (!conversation.user) { continue; }

      if (conversation?.user?.locationId && !allowedLocationsMap[conversation.user.locationId]) {
        continue; // Skip conversations from locations that are not allowed
      }

      if (!matchesSearchTerm(conversation.user, searchTerm)) { continue; }
      const escalations = conversation.escalations ? Object.values(conversation.escalations) : [];
      if (escalations.length > 0 && escalations.some((escalation) => (escalation.status === EscalationStatus.ACTIVE || escalation.status === EscalationStatus.ASSIGNED) && escalation.assignedMemberId === Member.id! && !escalation.resolved)) {
        escalatedConversations.push(conversation);
      } else {
        otherConversations.push(conversation);
      }
    }

    escalatedConversations = sortConversationsByMostRecentMessage(escalatedConversations);
    otherConversations = sortConversationsByMostRecentMessage(otherConversations);

    return { escalatedConversations, otherConversations };
  }, [conversations, searchTerm]);

  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20, scale: 0.95 }
  }

  return (
    <motion.div
      className="py-6 px-5 h-full overflow-y-auto [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-grey-400 [&::-webkit-scrollbar-thumb]:rounded-md"
      initial="hidden"
      key="open"
      animate="visible"
      exit="exit"
      variants={fadeVariants}
      transition={{ duration: 0.2 }}
    >
      {/* escalations & open conversations */}
      <div className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-grey-600">My Escalations</h2>
        <div className="mb-8">
          <AnimatePresence mode="popLayout">
            {escalatedConversations?.length ? escalatedConversations.map((conversation) => (
              <motion.div
                animate="visible"
                exit="exit"
                initial="hidden"
                variants={cardVariants}
                layout
                key={conversation?.user?.id}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <Link
                  className="w-full h-fit text-left rounded-md focus-visible:bg-grey-200 hover:text-black"
                  to={`/conversations/user/${conversation?.user?.id}`}
                >
                  <OpenConversationCard
                    // sessionMessageData={sessionMessageData}
                    conversation={conversation}
                    key={conversation?.user?.id}
                    selectedUserId={selectedUserId}
                    isEscalated={true}
                    isOnline={conversation?.user?.id ? usersOnline?.[conversation?.user?.id]?.online : false}
                  />
                </Link>
              </motion.div>
            )) : (
              <div className="p-6 text-center rounded-lg bg-grey-200" key="noEscalations">
                <p className="text-grey-600">{searchTerm?.length ? "No escalations found" : "Your escalations will appear here"}</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-grey-600">Other Conversations</h2>
        <div className="flex flex-col gap-0.5">
          <AnimatePresence mode="popLayout">
            {otherConversations?.length ? otherConversations.map((conversation) => (
              <motion.div
                animate="visible"
                exit="exit"
                initial="visible"
                variants={cardVariants}
                className="h-[58px]"
                layout
                key={conversation?.user?.id}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <Link
                  className="size-full text-left rounded-md focus-visible:bg-grey-200 hover:text-black"
                  to={`/conversations/user/${conversation?.user?.id}`}
                >
                  <OpenConversationCard
                    conversation={conversation}
                    key={conversation?.user?.id}
                    selectedUserId={selectedUserId}
                    isOnline={conversation?.user?.id ? usersOnline?.[conversation?.user?.id]?.online : false}
                    sessionMessageData={sessionMessageData}
                  />
                </Link>
              </motion.div>
            )) : (
              <div className="p-6 text-center rounded-lg bg-grey-200" key="noConversations">
                <p className="text-grey-600">{searchTerm?.length ? "No conversations found" : "No open conversations"}</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  )
}
